import React, { useCallback, useEffect, useState } from 'react';
import ApiClient from '../utils/ApiClient';

/**
 * ASIN alias manager (owner, 2026-09-03): a product can sell under several ASINs — the live case
 * is the DITRA-HEAT repair kit, whose authorized ASIN had a dead listing while an alternate
 * carried all the demand. This page maintains the mapping; the PO panel's split modal is where
 * it earns its keep (choose units per ASIN, with rank/velocity/stock context).
 */
export default function AsinAliases() {
    const [aliases, setAliases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [form, setForm] = useState({ itemCode: '', asin: '', role: 'ALTERNATE', note: '' });
    const [saving, setSaving] = useState(false);
    // Owner, 2026-09-03: the silent FBA-conversion failure must be VISIBLE and fixable — on
    // demand, never a cron. A listing created with FBA intent but stuck on the merchant channel
    // is un-inboundable, and Amazon's error blames "recently added" instead of the channel.
    const [mismatches, setMismatches] = useState([]);
    const [converting, setConverting] = useState(null);
    const [convertNote, setConvertNote] = useState(null);

    const loadMismatches = useCallback(async () => {
        try {
            const res = await ApiClient.get('/api/listings/channel-mismatches');
            setMismatches(res.data?.mismatches || []);
        } catch (err) {
            console.error('Channel mismatch check failed:', err);
        }
    }, []);

    useEffect(() => { loadMismatches(); }, [loadMismatches]);

    const convertToFba = async (row) => {
        setConverting(row.seller_sku);
        setConvertNote(null);
        try {
            const res = await ApiClient.post('/api/listings/convert-to-fba',
                { sku: row.seller_sku, asin: row.asin1 });
            setConvertNote(`${row.seller_sku}: Amazon ${res.data?.status || 'accepted'} the conversion`
                + (res.data?.issues ? ' — with issues, check the listing' : '')
                + '. The channel updates after Amazon processes the change.');
            await loadMismatches();
        } catch (err) {
            setConvertNote(`${row.seller_sku}: conversion failed — ${err.response?.data?.error || err.message}`);
        }
        setConverting(null);
    };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await ApiClient.get('/api/shipment-planner/asin-aliases');
            setAliases(res.data?.aliases || []);
            setError(null);
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const add = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        try {
            await ApiClient.post('/api/shipment-planner/asin-aliases', form);
            setForm({ itemCode: '', asin: '', role: 'ALTERNATE', note: '' });
            await load();
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        }
        setSaving(false);
    };

    const remove = async (row) => {
        if (!window.confirm(`Remove the mapping ${row.item_code} → ${row.asin}? The split modal will stop offering this ASIN.`)) return;
        try {
            await ApiClient.delete(`/api/shipment-planner/asin-aliases/${row.id}`);
            await load();
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        }
    };

    return (
        <div className="p-6 max-w-4xl mx-auto">
            <h1 className="text-2xl font-semibold text-gray-900">ASIN aliases</h1>
            <p className="text-sm text-gray-500 mt-1 mb-5">
                Products that sell under more than one ASIN. When a PO line's product is mapped here,
                adding it to a shipment plan asks how many units go to each ASIN — with rank, velocity,
                and stock shown per listing.
            </p>

            {error && (
                <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
                    {error}
                </div>
            )}

            {/* FBA channel health — the stuck-conversion trap, surfaced on demand. */}
            <div className="mb-6 rounded-md border border-amber-200 bg-amber-50/60 px-4 py-3">
                <h2 className="text-sm font-semibold text-amber-900 mb-1">FBA channel mismatches</h2>
                {convertNote && <p className="text-xs text-amber-800 mb-2">{convertNote}</p>}
                {mismatches.length === 0 ? (
                    <p className="text-xs text-gray-600">
                        All FBA-intent listings carry the FBA channel — nothing stuck.
                    </p>
                ) : (
                    <div className="space-y-1">
                        <p className="text-xs text-amber-800 mb-2">
                            These FBA-prefixed SKUs are stuck on the merchant channel — Amazon refuses
                            them on inbound plans until converted.
                        </p>
                        {mismatches.map(row => (
                            <div key={row.seller_sku} className="flex items-center justify-between text-sm bg-white rounded border border-amber-200 px-3 py-1.5">
                                <span>
                                    <span className="font-mono">{row.seller_sku}</span>
                                    <span className="text-gray-500 ml-2">{row.asin1}</span>
                                    <span className="text-gray-400 ml-2 text-xs">{row.status} · listed {String(row.open_date || '').slice(0, 10)}</span>
                                </span>
                                <button
                                    onClick={() => convertToFba(row)}
                                    disabled={converting === row.seller_sku}
                                    className="px-2.5 py-1 text-xs rounded border border-amber-400 text-amber-800 hover:bg-amber-100 disabled:opacity-50"
                                >
                                    {converting === row.seller_sku ? 'Converting…' : 'Convert to FBA'}
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <form onSubmit={add} className="mb-6 flex flex-wrap items-end gap-3 rounded-md border border-gray-200 bg-gray-50 px-4 py-3">
                <label className="text-sm text-gray-700">
                    <span className="block text-xs font-medium text-gray-500 mb-1">Item code</span>
                    <input aria-label="Item code" value={form.itemCode}
                           onChange={e => setForm(f => ({ ...f, itemCode: e.target.value }))}
                           className="rounded border-gray-300 text-sm w-36" required />
                </label>
                <label className="text-sm text-gray-700">
                    <span className="block text-xs font-medium text-gray-500 mb-1">ASIN</span>
                    <input aria-label="ASIN" value={form.asin}
                           onChange={e => setForm(f => ({ ...f, asin: e.target.value }))}
                           className="rounded border-gray-300 text-sm font-mono w-36" required />
                </label>
                <label className="text-sm text-gray-700">
                    <span className="block text-xs font-medium text-gray-500 mb-1">Role</span>
                    <select aria-label="Role" value={form.role}
                            onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                            className="rounded border-gray-300 text-sm">
                        <option value="ALTERNATE">Alternate</option>
                        <option value="AUTHORIZED">Authorized</option>
                    </select>
                </label>
                <label className="flex-1 min-w-[180px] text-sm text-gray-700">
                    <span className="block text-xs font-medium text-gray-500 mb-1">Note</span>
                    <input aria-label="Note" value={form.note}
                           onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
                           className="rounded border-gray-300 text-sm w-full" />
                </label>
                <button type="submit" disabled={saving}
                        className="px-4 py-2 text-sm rounded-md bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-50">
                    Add alias
                </button>
            </form>

            {loading ? (
                <div className="text-sm text-gray-400">Loading…</div>
            ) : aliases.length === 0 ? (
                <div className="text-sm text-gray-400 italic">
                    No aliases yet — every product is single-ASIN as far as the planner knows.
                </div>
            ) : (
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-3 py-2 text-left font-medium text-gray-500">Item code</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-500">ASIN</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-500">Role</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-500">Note</th>
                            <th className="px-3 py-2" />
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {aliases.map(row => (
                            <tr key={row.id}>
                                <td className="px-3 py-2 font-mono text-xs">{row.item_code}</td>
                                <td className="px-3 py-2 font-mono text-xs">{row.asin}</td>
                                <td className="px-3 py-2">
                                    <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${
                                        row.role === 'AUTHORIZED'
                                            ? 'bg-indigo-100 text-indigo-700'
                                            : 'bg-gray-100 text-gray-600'}`}>
                                        {row.role === 'AUTHORIZED' ? 'authorized' : 'alternate'}
                                    </span>
                                </td>
                                <td className="px-3 py-2 text-gray-500">{row.note}</td>
                                <td className="px-3 py-2 text-right">
                                    <button onClick={() => remove(row)}
                                            className="text-xs text-red-600 hover:text-red-800 underline">
                                        Remove
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}
